import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { IMAGES, CONTENT } from "../data/site";
import { scrollToSection } from "./Header";

export const Hero = () => {
  const LINES = [
    { plain: CONTENT.heroLine1Plain, accent: CONTENT.heroLine1Accent },
    { plain: CONTENT.heroLine2Plain, accent: CONTENT.heroLine2Accent },
  ];
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="home" ref={ref} data-testid="hero-section" className="relative flex min-h-screen flex-col justify-center overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <img
          src={IMAGES.hero}
          alt="Steel structural framework at a construction site"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-black/40" />
        <div className="absolute inset-0 blueprint-grid opacity-60" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative mx-auto w-full max-w-7xl px-6 pt-32 pb-24 md:px-12">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.7 }}
          className="mb-8 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.3em] text-[#3377FF]"
          data-testid="hero-overline"
        >
          <span className="h-px w-10 bg-[#0055FF]" />
          Structural Fabrication · Roofing · Prefab — Hyderabad
        </motion.p>

        <h1 className="font-display font-extrabold uppercase leading-[0.98] tracking-tighter" data-testid="hero-headline">
          {LINES.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                className="block text-4xl sm:text-6xl lg:text-[5.5rem]"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1, delay: 0.35 + i * 0.16, ease: [0.16, 1, 0.3, 1] }}
              >
                {line.plain}
                <span className="text-[#0055FF]">{line.accent}</span>
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-[#A3A3A3]"
          data-testid="hero-subtext"
        >
          {CONTENT.heroSubtext}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.8 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-quote-button"
            onClick={() => scrollToSection("#contact")}
            className="group flex items-center gap-3 bg-[#0055FF] px-8 py-4 font-display text-sm font-bold uppercase tracking-widest text-white transition-all hover:bg-[#3377FF] hover:scale-[1.03]"
          >
            Get a Free Quote
            <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
          </button>
          <button
            data-testid="hero-services-button"
            onClick={() => scrollToSection("#services")}
            className="flex items-center gap-3 border border-white/25 px-8 py-4 font-display text-sm font-bold uppercase tracking-widest text-white transition-all hover:border-[#0055FF] hover:text-[#3377FF]"
          >
            Explore Our Services
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute inset-x-0 bottom-0 hidden items-center justify-between border-t border-white/10 px-6 py-4 text-[11px] uppercase tracking-[0.25em] text-white/50 md:flex md:px-12"
      >
        <span data-testid="hero-location">{CONTENT.address}</span>
        <span className="flex items-center gap-2">
          Scroll <ArrowDown size={13} className="animate-bounce" />
        </span>
        <span data-testid="hero-phone">{CONTENT.phone}</span>
      </motion.div>
    </section>
  );
};
